import { AfterViewInit, Component, inject, ChangeDetectionStrategy, signal } from '@angular/core';
import {MAT_DIALOG_DATA, MatDialogRef} from '@angular/material/dialog';
import {TagInformation} from '../../../model/tag-information';
import Handsontable from 'handsontable';
import { MatButton } from '@angular/material/button';


@Component({
    selector: 'app-tag-management-dialog',
    templateUrl: './tag-management-dialog.component.html',
    styleUrls: ['./tag-management-dialog.component.css'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [MatButton]
})
export class TagManagementDialogComponent implements AfterViewInit {
  private dialogRef = inject<MatDialogRef<TagManagementDialogComponent>>(MatDialogRef);
  private data = inject(MAT_DIALOG_DATA);


  tags: TagInformation[];
  handsontable!: Handsontable;
  readonly errorMessage = signal<string>(undefined!);
  tableHeight: number;
  preventQuantityChanges: boolean;

  constructor() {
    const tagDetails = this.data.tags;
    this.tableHeight = this.data.tableHeight || 640;
    if (this.data.quantity > tagDetails.length) {
      for (let index = tagDetails.length; index < this.data.quantity; index++) {
        const tag: TagInformation = new TagInformation();
        tag.sequence = index + 1;
        tagDetails.push(tag);
      }
    }
    this.tags = tagDetails;
    this.preventQuantityChanges = this.data.preventQuantityChanges || false;
  }

  cancel(): void {
    this.dialogRef.close(null);
  }

  save(): void {
    this.dialogRef.close(this.tags);
  }

  ngAfterViewInit(): void {
    const options = {
      data: this.tags,
      colHeaders: ['Sequence', 'Tag', 'Riser/Loop', 'Floor', 'Room', 'Building', 'PO Line'],
      colWidths: [20],
      columns: [
        {
          data: 'sequence',
          readOnly: true,
          className: 'read-only-column',
        },
        {
          data: 'tag',
          validator: (value: any, callback: any) => this.validateLength(value, callback, 14, 'Tag'),
          allowInvalid: false
        },
        {
          data: 'riser',
          validator: (value: any, callback: any) => this.validateLength(value, callback, 20, 'Riser/Loop'),
          allowInvalid: false
        },
        {
          data: 'floor',
          validator: (value: any, callback: any) => this.validateLength(value, callback, 5, 'Floor'),
          allowInvalid: false
        },
        {
          data: 'room',
          validator: (value: any, callback: any) => this.validateLength(value, callback, 20, 'Room'),
          allowInvalid: false
        },
        {
          data: 'building',
          validator: (value: any, callback: any) => this.validateLength(value, callback, 20, 'Building'),
          allowInvalid: false
        },
        {
          data: 'poLine',
          validator: (value: any, callback: any) => this.validateLength(value, callback, 5, 'PO Line'),
          allowInvalid: false
        },
      ],
      allowInsertRow: !this.preventQuantityChanges,
      allowRemoveRow: !this.preventQuantityChanges,
      contextMenu: this.preventQuantityChanges ? undefined : ['row_above', 'row_below', '---------', 'remove_row'],
      height: this.tableHeight,
      stretchH: 'all',
      allowInsertColumn: false,
      allowRemoveColumn: false,
      fillHandle: {
        direction: 'vertical',
        autoInsertRow: false
      }
    };
    const tagTable = document.getElementById('tagsTable');
    this.handsontable = new Handsontable(tagTable!, options);
    this.handsontable.addHook('beforeAutofill', [(start: any, end: any, data: any) => this.beforeAutofill(start, end, data)]);
    this.handsontable.addHook('beforeChange', [(changes: any, src: any) => this.beforeTagsChanged(changes, src)]);
    this.handsontable.addHook('beforeRemoveRow', [(index: any, amount: any) => this.beforeRowRemoved(index, amount)]);
  }

  beforeRowRemoved(index: number, amount: number): boolean {
    if (this.preventQuantityChanges) {
      return false;
    }
    if (this.handsontable.countRows() <= amount) {
      alert('Please delete the line instead of removing all tags');
      return false;
    }
    return true;
  }

  validateLength(value: string, callback: any, maxLength: number, fieldName: string): void {
    const isValid: boolean = this.lengthIsValid(value, maxLength);
    if (!isValid) {
      this.errorMessage.set(fieldName + ' can only be ' + maxLength + ' characters long');
    } else {
      this.errorMessage.set(null!);
    }
    callback(isValid);
  }

  private lengthIsValid(value: string, maxLength: number): boolean {
    return (!value || value.length <= maxLength);
  }

  beforeTagsChanged(changes: any, src: any): boolean {
    return src !== 'Autofill.fill';
  }

  beforeAutofill(start: any, end: any, data: any): boolean {
    const tagsGrid = this.handsontable;
    const startCol = start.col;
    const startRow = start.row;
    const endCol = end.col;
    const endRow = end.row;
    const intPattern = /\d+/g;
    let numberFound;
    let currentColumn = startCol;
    let currentRow = startRow;
    if (!data || data.length === 0) {
      return false;
    }
    const valueToCopy = data[0][0].toString();
    numberFound = valueToCopy.match(intPattern);
    if (numberFound && numberFound.length > 0) {
      const numberToReplace = numberFound[numberFound.length - 1];
      let replacementNumber = +numberToReplace;
      for (currentColumn = startCol; currentColumn <= endCol; currentColumn++) {
        for (currentRow = startRow; currentRow <= endRow; currentRow++) {
          tagsGrid.setDataAtCell(currentRow, currentColumn,
            valueToCopy.replace(numberToReplace, (++replacementNumber).toString()), 'autofill-override');
        }
      }
    } else {
      for (currentColumn = startCol; currentColumn <= endCol; currentColumn++) {
        for (currentRow = startRow; currentRow <= endRow; currentRow++) {
          tagsGrid.setDataAtCell(currentRow, currentColumn, valueToCopy, 'autofill-override');
        }
      }
    }
    return true;
  }

}
